/**
 * Phase 7 · Reservations: diary list with status progression
 * (pending → confirmed → seated → completed / cancelled / no-show).
 */
import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { http, ApiError } from '../api/client';
import { useUi } from '../store/ui';
import { dateTime } from '../lib/format';
import { DataTable, type Column } from '../components/ui/DataTable';
import { Button } from '../components/ui/Button';
import { StatusBadge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';

interface Reservation { id: string; guest_name: string; phone: string | null; guest_count: number; starts_at: string; status: string; table_label?: string | null; notes?: string | null }

const NEXT: Record<string, { label: string; status: string }[]> = {
  pending: [{ label: 'Confirm', status: 'confirmed' }, { label: 'Cancel', status: 'cancelled' }],
  confirmed: [{ label: 'Seat', status: 'seated' }, { label: 'No-show', status: 'no_show' }],
  seated: [{ label: 'Complete', status: 'completed' }],
};

export function ReservationsPage() {
  const toast = useUi((s) => s.toast);
  const [rows, setRows] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await http.get<{ reservations: Reservation[] }>('/crm/reservations')).reservations); }
    catch (e) { toast({ kind: 'error', title: 'Load failed', message: e instanceof ApiError ? e.message : String(e) }); }
    finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { void load(); }, [load]);

  const advance = async (r: Reservation, status: string) => {
    try {
      await http.post(`/crm/reservations/${r.id}/status`, { status });
      toast({ kind: 'success', title: `${r.guest_name} → ${status.replace('_', ' ')}` });
      await load();
    } catch (e) { toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) }); }
  };

  const cols: Column<Reservation>[] = [
    { key: 'starts_at', header: 'When', sortable: true, render: (r) => dateTime(r.starts_at) },
    { key: 'guest_name', header: 'Guest' },
    { key: 'guest_count', header: 'Covers' },
    { key: 'phone', header: 'Phone', render: (r) => r.phone ?? '—' },
    { key: 'table_label', header: 'Table', render: (r) => r.table_label ?? '—' },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    {
      key: 'act', header: '', render: (r) => (
        <div className="flex justify-end gap-2">
          {(NEXT[r.status] ?? []).map((a) => (
            <Button key={a.status} size="sm" variant={a.status === 'cancelled' || a.status === 'no_show' ? 'ghost' : 'secondary'} onClick={() => void advance(r, a.status)}>
              {a.label}
            </Button>
          ))}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-ink">Reservations</h1>
        <Button onClick={() => setModal(true)}><Plus size={16} /> New reservation</Button>
      </header>
      <DataTable columns={cols} rows={rows} loading={loading} />
      {modal && <NewModal onClose={() => setModal(false)} onSaved={() => { setModal(false); void load(); }} />}
    </div>
  );
}

function NewModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const toast = useUi((s) => s.toast);
  const [guestName, setGuestName] = useState('');
  const [phone, setPhone] = useState('');
  const [guestCount, setGuestCount] = useState('2');
  const [startsAt, setStartsAt] = useState(() => {
    const d = new Date(Date.now() + 3600e3);
    d.setMinutes(0, 0, 0);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await http.post('/crm/reservations', { guestName, phone: phone || null, guestCount: parseInt(guestCount, 10), startsAt: new Date(startsAt).toISOString() });
      toast({ kind: 'success', title: 'Reservation booked', message: guestName });
      onSaved();
    } catch (e) { toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) }); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="New reservation"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={() => void save()}>Book</Button></>}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Input label="Guest name" value={guestName} onChange={(e) => setGuestName(e.target.value)} required />
        <Input label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <Input label="Covers" type="number" min="1" max="60" value={guestCount} onChange={(e) => setGuestCount(e.target.value)} />
        <Input label="Date & time" type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
      </div>
    </Modal>
  );
}
